import {CBARMaterialProperty, ShaderUniform} from "./CBARMaterial";
import * as THREE from "three";
import {
    MaskFunctions,
    MaskUniforms,
    SerializeUniforms,
    StandardUniforms,
    UniformedDictionary
} from "./CBARShaderFunctions";
import {iCBARMaskedMaterial} from "./iCBARMaskedMaterial";
import {Material} from "three/src/materials/Material";
import {parseIncludes} from "../internal/Utils";
import {CBARContext} from "../CBARContext";

export class CBARMaskedMaterial implements iCBARMaskedMaterial {

    constructor(public context:CBARContext, maskTexture:THREE.Texture) {
        this.uniforms = StandardUniforms(context.gl.resolution);
        //uniform sampler2D texture;
        this.uniforms["u_background"] = {type:"sampler2D", value:context.gl.scene.background};
        this.uniforms["u_mask"] = {type:"sampler2D", value:maskTexture};
        this.uniforms["u_diffuse"] = {type:"vec4", value:this._color};

        this._material = new THREE.ShaderMaterial({uniforms: this.uniforms,
            fragmentShader: this.fragmentShader(), vertexShader:this.vertexShader()});

        this._material.onBeforeCompile = (shader) => {
            //console.log(parseIncludes(shader.fragmentShader));
            this._shader = shader;
        }
    }

    private vertexShader = ()=> `
        void main() {
            gl_Position = vec4( position, 1.0 );
        }
    `;

    private fragmentShader = ()=> `
        ${SerializeUniforms(this.uniforms)}
        ${MaskFunctions.join('\n')}
        
        void main() {
            vec4 diffuseColor = u_diffuse;
            vec2 blendRadius = u_maskBlendDistance / u_viewportSize;
            
            vec2 pos = clamp(gl_FragCoord.xy / u_viewportSize, 0.0, 1.0);
            vec2 maskPos = cropPosition(pos, u_maskCrop);
            
            diffuseColor.a *= texture2D(u_mask, maskPos).g;
            
            if (diffuseColor.a < 0.01) discard;
            
            vec4 background = texture2D(u_background, pos);
            float luminance = clamp(u_lightingFactor * background.g + u_lightingOffset, 0.0, 1.0);
            
            gl_FragColor.rgb = mix(background.rgb, diffuseColor.rgb * luminance, diffuseColor.a);
        }
    `;

    public readonly uniforms:UniformedDictionary;
    private _shader?:THREE.Shader;
    private readonly _material:Material;

    public get threeMaterial()  {
        return this._material;
    }

    public getUniform(name:string) : any {
        return this._shader ? this._shader.uniforms[name].value : this.uniforms[name].value;
    }

    public setUniform(name:string, value:any) {
        if (this._shader) {
            return this._shader.uniforms[name].value = value
        } else {
            this.uniforms[name].value = value;
        }
    }

    private _color = new THREE.Vector4(1,1,0,1.0);

    setMaterialProperty(name:string, value:any):void {
        if (name === CBARMaterialProperty.color) {
            const color = new THREE.Color(value);
            this._color.set(color.r, color.g, color.b, 1.0);
            this.setUniform('u_diffuse', this._color);
        }
    }

    public get lightingFactor() : number {
        return this.getUniform("u_lightingFactor");
    }

    public set lightingFactor(value:number) {
        this.setUniform("u_lightingFactor", value);
    }

    public get lightingOffset() : number {
        return this.getUniform("u_lightingOffset");
    }

    public set lightingOffset(value:number) {
        this.setUniform("u_lightingOffset", value);
    }

    public get smoothStep() : number {
        return this.getUniform("u_smoothStep");
    }

    public set smoothStep(value:number) {
        this.setUniform("u_smoothStep", value);
    }

    public get maskIndex() : number {
        return this.getUniform("u_maskIndex");
    }

    public set maskIndex(value:number) {
        this.setUniform("u_maskIndex", value);
    }

    public get maskBlendDistance() : number {
        return this.getUniform("u_maskBlendDistance");
    }

    public set maskBlendDistance(value:number) {
        this.setUniform("u_maskBlendDistance", value);
    }

    public get maskCrop() : THREE.Vector4 {
        return this.getUniform("u_maskCrop");
    }

    public set maskCrop(value:THREE.Vector4) {
        this.setUniform("u_maskCrop", value);
    }

}